import { sanitizeUrl } from "@braintree/sanitize-url";
import type { Location } from "react-router-dom";

import { AUTH_PATHS, DEFAULT_PROTECTED_ROUTE_PATH } from "../constants";

const POST_LOGIN_REDIRECT_KEY = "postLoginRedirectPath";

// Auth pages (sign in, sign up, update-password, OAuth callback, ...) are steps
// within the auth flow itself, never a valid post-auth destination. Without this
// guard, a re-render mid-flow (e.g. ProtectedRoute evaluating at /auth/update
// while a BAA signature is pending) can capture an /auth/* path as the redirect
// target, looping the user back into the auth flow instead of their destination.
const isAuthPath = (path: string): boolean =>
  Object.values(AUTH_PATHS).some(
    (authPath) =>
      path === authPath || path.startsWith(`${authPath}?`) || path.startsWith(`${authPath}#`)
  );

/**
 * Sanitizes a redirect path to prevent open-redirect, protocol injection, and path traversal attacks.
 *
 * Rules enforced:
 * - Must be a string starting with a single "/" (relative path only).
 * - Rejects protocol-relative URLs ("//...") and any protocol-prefixed values ("http:", "javascript:", etc.).
 * - Removes null bytes and trims whitespace.
 * - Blocks directory traversal patterns ("../" or "..\\").
 *
 * Returns a safe fallback path if the input is invalid or unsafe.
 */
export const sanitizeRedirectPath: (
  path: string | null | undefined,
  fallbackPath?: string
) => string = (
  path: string | null | undefined,
  fallbackPath: string = DEFAULT_PROTECTED_ROUTE_PATH
): string => {
  if (!path || typeof path !== "string") return fallbackPath;

  // First pass: Apply @braintree/sanitize-url for additional security
  let urlSanitized: string;
  try {
    urlSanitized = sanitizeUrl(path);
  } catch (error) {
    console.error(error);
    // If sanitizeUrl fails (e.g., URL.canParse not available), fall back to manual checks
    urlSanitized = path;
  }

  // If sanitizeUrl returns "about:blank" or empty, the URL was unsafe
  if (!urlSanitized || urlSanitized === "about:blank") {
    return fallbackPath;
  }

  const sanitized = urlSanitized.replace(/\0/g, "").trim();

  // Must be a relative path starting with "/" but not "//" (protocol-relative)
  // No external URLs, protocols, or path traversal
  if (
    !sanitized.startsWith("/") ||
    sanitized.startsWith("//") ||
    /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(sanitized) ||
    sanitized.includes("../") ||
    sanitized.includes("..\\")
  ) {
    return fallbackPath;
  }

  // Auth pages are flow steps, never a valid post-auth destination (see isAuthPath).
  if (isAuthPath(sanitized)) {
    return fallbackPath;
  }

  return sanitized;
};

/**
 * Validates that a URL is an allowed redirection target.
 * Only allows relative URLs or same-origin URLs.
 */
const isValidRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

const buildTargetPath = ({
  pathname = "/",
  search = "",
  hash = "",
}: {
  pathname?: string;
  search?: string;
  hash?: string;
}): string => {
  const target = `${pathname}${search}${hash}`;
  return target.startsWith("/") ? target : `/${target}`;
};

const checkAndStoreRedirectPath = (path: string): void => {
  if (path) {
    try {
      window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    } catch (error) {
      console.error("Unable to store redirect path:", error);
    }
  }
};

interface LocationState {
  redirectPath?: string;
}

export const capturePreAuthRedirect = ({
  location,
  fallbackPath = DEFAULT_PROTECTED_ROUTE_PATH,
  extraState = {},
}: {
  location: Location;
  fallbackPath?: string;
  extraState?: Record<string, unknown>;
}): { replace: boolean; state: { redirectPath: string } } => {
  let target = fallbackPath;

  target = buildTargetPath({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });

  const safeTarget = sanitizeRedirectPath(target, fallbackPath);

  checkAndStoreRedirectPath(safeTarget);

  return {
    replace: true,
    state: { redirectPath: safeTarget, ...extraState },
  };
};

export const getPostAuthRedirect = ({
  location,
  fallbackPath = DEFAULT_PROTECTED_ROUTE_PATH,
}: {
  location?: Location;
  fallbackPath?: string;
} = {}): { redirectPath: string } => {
  // Stage 1: Check location.state first (if available)
  if ((location?.state as LocationState)?.redirectPath) {
    const { redirectPath } = location?.state as LocationState;
    const safePath = sanitizeRedirectPath(redirectPath, fallbackPath);
    if (isValidRedirectUrl(safePath)) {
      if (window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)) {
        window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      }
      return { redirectPath: safePath };
    }
  }

  // Stage 2: Fall back to sessionStorage (current approach)
  try {
    const stored = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (stored) {
      window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      const safePath = sanitizeRedirectPath(stored, fallbackPath);
      if (isValidRedirectUrl(safePath)) {
        return { redirectPath: safePath };
      }
    }
  } catch (error) {
    console.error("Unable to read redirect path from sessionStorage:", error);
  }

  return { redirectPath: fallbackPath };
};
